import React, { useEffect } from 'react';
import { useStore } from 'effector-react';
import { toFlag, toPercent } from 'pano-utils';
import { Scrollbars } from 'react-custom-scrollbars';
import { ImageType, type Mask, type MaskSetting, SceneType } from '@types';
import { SYS_MASK } from '@constants';
import { sendSuccess } from '@components/Noti';
import Title from '@components/Title';
import { Collapse, CollapseWrapper } from '@components/Collapse';
import ImageContainer from '@components/ImageContainer';
import { $sceneMapApi } from '@stores/dataStores';
import Image from '@components/Image';
import Tip from '@components/Tip';
import Button from '@components/Button';
import Slider from '@components/Slider';
import { $loadedScene } from '@stores/loadedStore';
import FeatureToggle from '@components/FeatureToggle';
import { MainPlayer } from '@stores/krpPlayerStores';
import { getMediaUrl, getSysUrl } from '@utils/urlMakers';
import { showApplyWarning } from '@components/ConfirmModal';
import { selectLibImages } from '@modules/mediaLib';
import SidebarHeader from '../components/SidebarHeader';
import SidebarApplyTo from '../components/SidebarApplyTo';
import { showSceneModal } from '../../category';

const defaultSetting: MaskSetting = {
  stick: 1,
  scale: 1,
  rotate: 0,
};

type MKey = keyof Mask;

function SidebarMask() {
  const { mask, id } = useStore($loadedScene)!;
  const player = MainPlayer.get();

  useEffect(() => {
    if (mask?.top?.mediaUrl) {
      addMask('top', mask?.top);
    }
    if (mask?.bottom?.mediaUrl) {
      addMask('bottom', mask?.bottom);
    }
    return () => {
      player?.removeHotspotById(getMaskId('top'));
      player?.removeHotspotById(getMaskId('bottom'));
    };
  }, []);

  const getMaskId = (key: MKey) => `mask_${key}`;

  const updateValue =
    (key: MKey) =>
    <T extends keyof MaskSetting>(k: T, v: MaskSetting[T]) => {
      $sceneMapApi.updateScene({
        id,
        updater: (draft) => {
          draft.mask[key]![k] = v;
        },
      });
    };

  const addMask = (key: MKey, data: MaskSetting) => {
    const url = data.mediaId
      ? getMediaUrl(data.mediaUrl)
      : getSysUrl(data.mediaUrl);
    player?.addImageHotspot({
      id: getMaskId(key),
      urls: [url],
      ath: 0,
      atv: key === 'top' ? -90 : 90,
      scaleX: data.scale,
      scaleY: data.scale,
      distorted: !!data.stick,
      rotate: data.rotate,
      enabled: false,
    });
  };

  const handleImageSelect = (key: MKey) => async () => {
    const { libItems, sysItems } = await selectLibImages({
      defaultData: SYS_MASK,
    });
    let mediaId = 0;
    let url = '';
    if (libItems) {
      mediaId = libItems[0].id;
      url = libItems[0].url;
    } else if (sysItems) {
      mediaId = 0;
      url = sysItems[0].url;
    }
    if (!url) return;
    const setting: MaskSetting = {
      ...defaultSetting,
      ...(mask?.[key] ?? {}),
      enabled: 1,
      mediaId,
      mediaUrl: url,
    };
    $sceneMapApi.updateScene({
      id,
      updater: (draft) => {
        draft.mask[key] = setting;
      },
    });
    addMask(key, setting);
    player?.lookToHotspot({ id: getMaskId(key) });
  };

  const handleDelete = (key: MKey) => () => {
    $sceneMapApi.updateScene({
      id,
      updater: (draft) => {
        draft.mask[key] = {};
      },
    });
    player?.removeHotspotById(getMaskId(key));
  };

  const handleScaleChange = (key: MKey) => (val: number) => {
    updateValue(key)('scale', val);
    player?.updateImageHotspot({
      id: getMaskId(key),
      scaleX: val,
      scaleY: val,
    });
  };

  const handleRotateChange = (key: MKey) => (val: number) => {
    updateValue(key)('rotate', val);
    player?.updateImageHotspot({
      id: getMaskId(key),
      rotate: val,
    });
  };

  const handleFloatChange = (key: MKey) => (bool: boolean) => {
    updateValue(key)('stick', toFlag(bool));
    player?.updateImageHotspot({
      id: getMaskId(key),
      distorted: bool,
    });
  };

  const handleApplyTo = (key: MKey) => async () => {
    const setting = mask?.[key] ?? {};
    if (!setting.mediaUrl) {
      const { confirmed } = await showApplyWarning();
      if (!confirmed) return;
    }
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      multiple: true,
      allowedTypes: [SceneType.pano, SceneType.panoVideo],
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        const x = draft.get(_id)!;
        if (!x.mask) x.mask = {};
        x.mask[key] = setting;
      });
    });
    sendSuccess('遮罩设置已应用到所选场景');
  };

  const renderSetting = (key: MKey) => {
    const data = mask?.[key] ?? {};
    return (
      <>
        <ImageContainer
          className="h-115"
          placeholder="支持PNG、JPG格式图片"
          onDeleteClick={handleDelete(key)}
        >
          {!!data.mediaUrl && (
            <Image
              src={data.mediaUrl}
              type={data.mediaId ? ImageType.media : ImageType.sysMedia}
              className="max-h-full max-w-full"
            />
          )}
        </ImageContainer>
        <div className="mt-10 flex items-center justify-between">
          <Tip>建议尺寸：500x500</Tip>
          <Button size="sm" onClick={handleImageSelect(key)}>
            选择图片
          </Button>
        </div>
        {!!data.mediaUrl && (
          <>
            <Slider
              className="mt-20"
              min={0.2}
              max={2}
              value={data.scale}
              valToString={(x) => toPercent(x, 0)}
              onChange={handleScaleChange(key)}
            >
              <Slider.Header title="缩放比例：" />
              <Slider.Bar step={0.01} />
              <Slider.Footer />
            </Slider>
            <Slider
              className="mt-20"
              min={0}
              max={360}
              value={data.rotate}
              valToString={(x) => `${x}°`}
              onChange={handleRotateChange(key)}
            >
              <Slider.Header title="旋转角度：" />
              <Slider.Bar step={1} />
              <Slider.Footer />
            </Slider>
            <FeatureToggle
              className="mt-20"
              title="遮罩跟随场景转动"
              isOn={!!data.stick}
              onToggle={handleFloatChange(key)}
            />
          </>
        )}
        <SidebarApplyTo
          className="mt-20"
          title="把遮罩应用到…"
          onClick={handleApplyTo(key)}
        />
      </>
    );
  };

  return (
    <Scrollbars>
      <CollapseWrapper>
        <SidebarHeader title={<Title>遮罩设置</Title>} />
        <Collapse title="天空遮罩">{renderSetting('top')}</Collapse>
        <Collapse title="地面遮罩">{renderSetting('bottom')}</Collapse>
      </CollapseWrapper>
    </Scrollbars>
  );
}

export default SidebarMask;
